import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiFetch } from '@/integrations/api/client';
import { Loader2, Lock, Eye, EyeOff, ShieldAlert, ArrowLeft } from 'lucide-react';

const Logo = () => (
  <div className="flex items-center gap-2.5">
    <svg width="34" height="34" viewBox="0 0 64 64" fill="none"><rect width="64" height="64" rx="15" fill="#2563EB" /><rect x="12" y="42" width="8" height="16" rx="2" fill="white" opacity=".5" /><rect x="24" y="32" width="8" height="26" rx="2" fill="white" opacity=".7" /><rect x="36" y="22" width="8" height="36" rx="2" fill="white" /><rect x="48" y="12" width="8" height="46" rx="2" fill="#22C55E" /></svg>
    <span className="text-xl font-extrabold text-foreground">
      <span style={{ color: '#3b82f6' }}>Prodi</span>fy<span style={{ color: '#22c55e', fontSize: 13, verticalAlign: 'super' }}>360</span>
    </span>
  </div>
);

// Weak (red) <8 · Fair (amber) 8+ · Strong (green) 8+number+upper · Very Strong (blue) 12+ & special
export function passwordStrength(pw: string): { level: 0 | 1 | 2 | 3; label: string; color: string } {
  if (!pw || pw.length < 8) return { level: 0, label: 'Weak', color: '#ef4444' };
  const hasNum = /[0-9]/.test(pw), hasUpper = /[A-Z]/.test(pw), hasSpecial = /[^A-Za-z0-9]/.test(pw);
  if (pw.length >= 12 && hasNum && hasUpper && hasSpecial) return { level: 3, label: 'Very Strong', color: '#3b82f6' };
  if (hasNum && hasUpper) return { level: 2, label: 'Strong', color: '#22c55e' };
  return { level: 1, label: 'Fair', color: '#f59e0b' };
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useSafeNavigate();
  const { toast } = useToast();

  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [linkDead, setLinkDead] = useState(false);

  const strength = useMemo(() => passwordStrength(pw), [pw]);
  // Server requires min 8 + 1 number + 1 uppercase (level >= 2).
  const meetsPolicy = strength.level >= 2;
  const canSubmit = meetsPolicy && pw === confirm && !loading;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!meetsPolicy) { setError('Password must be at least 8 characters with a number and an uppercase letter.'); return; }
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      await apiFetch('/api/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, newPassword: pw }) });
      toast({ title: 'Password updated!', description: 'You can now sign in with your new password.' });
      navigate('/login', { replace: true });
    } catch (e: any) {
      const code = e?.code;
      if (e?.status === 400 && (code === 'invalid_token' || code === 'expired' || /invalid|expired/i.test(e.message || ''))) {
        setLinkDead(true);
      } else {
        setError(e.message || 'Could not reset password. Please try again.');
      }
    } finally { setLoading(false); }
  };

  if (!token || linkDead) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6 sm:p-10">
        <div className="w-full max-w-sm text-center animate-fade-in">
          <div className="mb-8 flex justify-center"><Logo /></div>
          <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-red-500/10 ring-1 ring-red-500/20 flex items-center justify-center">
            <ShieldAlert className="h-7 w-7 text-red-500" />
          </div>
          <h2 className="text-2xl font-bold tracking-tight">Reset link invalid</h2>
          <p className="text-sm text-muted-foreground mt-2">This password reset link is invalid or has expired. Reset links are valid for 1 hour and can only be used once.</p>
          <a href="/forgot-password" className="mt-7 inline-block">
            <Button className="h-11 px-6 text-[15px] font-semibold">Request a new link</Button>
          </a>
          <a href="/login" className="mt-5 flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6 sm:p-10">
      <div className="w-full max-w-sm animate-fade-in">
        <div className="mb-8 flex justify-center"><Logo /></div>
        <div className="mb-7 text-center">
          <h2 className="text-2xl font-bold tracking-tight">Set a new password</h2>
          <p className="text-sm text-muted-foreground mt-1">Choose a strong password you don't use elsewhere.</p>
        </div>

        {error && <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-2.5 text-sm text-red-500 font-medium">{error}</div>}

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rp-pw" className="text-xs font-medium">New password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input id="rp-pw" type={show ? 'text' : 'password'} autoComplete="new-password" placeholder="Min 8 chars, 1 number, 1 uppercase" className="pl-10 pr-10 h-11"
                value={pw} onChange={(e) => setPw(e.target.value)} disabled={loading} autoFocus />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" tabIndex={-1}>
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>

            {/* Strength meter */}
            {pw && (
              <div className="pt-1.5">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div key={i} className="h-1.5 flex-1 rounded-full transition-colors" style={{ background: i <= strength.level ? strength.color : 'hsl(var(--muted))' }} />
                  ))}
                </div>
                <p className="mt-1.5 text-xs font-medium" style={{ color: strength.color }}>{strength.label}</p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="rp-confirm" className="text-xs font-medium">Confirm password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input id="rp-confirm" type={show ? 'text' : 'password'} autoComplete="new-password" placeholder="Re-enter password" className="pl-10 h-11"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} disabled={loading} />
            </div>
            {confirm && pw !== confirm && <p className="text-xs text-red-500 font-medium">Passwords do not match</p>}
          </div>

          <Button type="submit" className="w-full h-11 text-[15px] font-semibold shadow-lg shadow-primary/20" disabled={!canSubmit}>
            {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Updating…</>) : 'Reset Password'}
          </Button>
        </form>

        <a href="/login" className="mt-6 flex items-center justify-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </a>
      </div>
    </div>
  );
}
